import * as React from 'react';
import {
    DocumentResponse,
    Metadata,
    ResourceRetentionPolicySummary,
    Submission,
    SubmissionRequestSummary,
    PortalTextCommon,
    PortalTextSubmissionDetails,
} from '../../Models/Api/strongbox.financialportal';

import '../Main.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowDownToLine,
    faXmark,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { SelectableDownloadFile } from '../GroupedFileDropdown/GroupedFileDropdown';
import { DownloadableFile } from '../../Utils/FileUtils';
import { StringFormat } from '../../Utils/StringUtils';

import { FileGroupSubmissionDetailsSummary } from './FileGroupSubmissionDetailsSummary';

/**
 * @member {submittedBy} is the email address (if present) of the actual human who directed the financial import
 * to occur.  In the shareable link scenario, this will be the email address of the person who received and used
 * the shareable link.  This is different than the person who requested the submission to be generated, i.e. the
 * logged in portal user who created the shareable link.
 * @member {submissioneRequestSummary} contains information about the options selected when the submission request
 * or shareable link was created including the person initiating the request, this would be the id of the user 
 * logged into the portal.
 * @member {containerClass} will be the last class listed on the className for the container. This can override
 * default settings like padding
 * */
type Props = {
    businessId: string;
    uploadDate: string;
    documents: DownloadableFile[];
    /** Whether the submission has any workbooks (they render in the card passed as children). */
    hasWorkbooks: boolean;
    retrievingFiles: boolean;
    noFilesAvailableMsg: string;
    verified: boolean;
    submittedBy: string | undefined;
    containerClass?: string;
    submission?: Submission;
    submissionMetadata?: Metadata[];
    submissionRequestSummary?: SubmissionRequestSummary;
    loadedUserDisplayName?: string;
    onDownload?: (files: DownloadableFile[], data: any | undefined) => void;
    onEmptyFile?: (file: DownloadableFile) => void;
    data?: any; // passed to onDownload
    retentionPolicySummary?: ResourceRetentionPolicySummary;
    /** Whether the narrow-viewport Collection details drawer is open (the page's title bar owns its button). */
    detailsDrawerOpen?: boolean;
    onCloseDetailsDrawer?: () => void;
    commonText?: PortalTextCommon;
    submissionDetailsText?: PortalTextSubmissionDetails;
    children?: React.ReactNode;
};

type FileCard = {
    cardKey: string;
    title: string;
    columnLabel: string;
    files: SelectableDownloadFile[];
    onSelection: (file: SelectableDownloadFile) => void;
    onDownload: () => void;
}

type FileSelectionGroup = {
    selections: boolean[];
}

// const xlsxContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export type FinancialDocument = DocumentResponse & { downloadUrl: string };

export const FileGroup: React.FC<Props> = (props): React.ReactElement => {
    const downloadFilesPrompt = props.submissionDetailsText?.downloadFilesPrompt || 'Download Files';

    // The single group of user uploaded documents (workbooks render in WorkbooksCard, which owns its own selection).
    const [fileGroupSelections, setFileGroupSelections] = React.useState<FileSelectionGroup[]>([]);
    const [userUploadedIndex, setUserUploadedIndex] = React.useState<number>(-1);


    const ValidIndexSet = (groupIndex: number, fileIndex: number): boolean => {
        return ((groupIndex >= 0) && (groupIndex < fileGroupSelections.length) && (fileIndex >= 0) && (fileIndex < fileGroupSelections[groupIndex].selections.length));
    }

    const createFileGroupSelectionsCopy = (): FileSelectionGroup[] => {
        return fileGroupSelections.map(groupSelections => {
            return {
                selections: groupSelections.selections.slice(),
            }
        });
    }

    React.useEffect(() => {
        const newSelections: FileSelectionGroup[] = [];
        if (props.documents) {
            newSelections.push({
                selections: props.documents.map(doc => false),
            });
            setUserUploadedIndex(newSelections.length - 1);
        } else {
            setUserUploadedIndex(-1);
        }
        setFileGroupSelections(newSelections);
    }, [props.documents]);

    const downloadSelectedGroupFiles = (groupIndex: number): void => {
        if ((!props.onDownload) || (groupIndex >= fileGroupSelections.length)) {
            return;
        }

        const group = fileGroupSelections[groupIndex];
        if (!group) {
            return;
        }

        const result: DownloadableFile[] = [];
        group.selections.forEach((selected, selectionIndex) => {
            if (selected) {
                result.push(props.documents[selectionIndex]);
            }
        });

        props.onDownload(result, props.data);
    }

    const hasUserFiles = (): boolean => {
        return ((props.documents) && (!!props.documents.length) && (userUploadedIndex !== -1));
    }

    // One bordered table card per group of files: a heading row on the page background, then a card with a
    // toolbar (the download action on the right), a column header
    // and one selectable row per file.
    const renderFileCard = (card: FileCard): React.ReactElement => {
        const noFiles = !props.retrievingFiles && card.files.length === 0;
        const hasSelections = card.files.some(file => file.selected);

        return (
            <div className={'page-section'} key={card.cardKey}>
                <div className={'page-section__heading'}>
                    <div className={'page-revision-bar'}>
                        <h2 className={'page-card-header-row__title'}>{card.title}</h2>
                    </div>
                </div>
                <div className={'cr-card page-section__body'}>
                    <div className={'page-workbook-toolbar'}>
                        <div className={'page-workbook-toolbar__actions'}>
                            <button
                                type={'button'}
                                className={'cr-btn cr-btn--secondary'}
                                disabled={!hasSelections}
                                onClick={() => card.onDownload()}
                            >
                                <span className={'cr-btn__icon'}><FontAwesomeIcon icon={faArrowDownToLine} /></span>
                                <span className={'cr-btn__label'}>{downloadFilesPrompt}</span>
                            </button>
                        </div>
                    </div>
                    {noFiles && (
                        <div className={'page-section__empty'}>{props.noFilesAvailableMsg}</div>
                    )}
                    {!noFiles && (
                        <React.Fragment>
                            <div className={'cr-table-cell cr-table-cell--column-header page-file-list-header'}>
                                <span className={'cr-table-cell__spacer'} />
                                <span className={'cr-table-cell__slot page-file-row'}>
                                    <span className={'page-file-row__check-spacer'} />
                                    <span className={'page-file-row__name cr-table-cell__header-text'}>{card.columnLabel}</span>
                                    <span className={'page-file-row__size cr-table-cell__header-text'}>{props.submissionDetailsText?.columnSize || 'Size'}</span>
                                </span>
                                <span className={'cr-table-cell__spacer'} />
                            </div>
                            <div className={'page-file-list'}>
                                {card.files.map((file, index) => {
                                    if (file.file.separator === true) {
                                        return (
                                            <div key={`${card.cardKey}-${index}`} className={'page-section__empty'}>{file.file.filename}</div>
                                        );
                                    }
                                    const select = (): void => {
                                        if (!file.file.isDeleted) {
                                            card.onSelection({ ...file, selected: !file.selected });
                                        }
                                    };
                                    return (
                                        <div className={'cr-table-cell'} key={`${card.cardKey}-${index}`}>
                                            <span className={'cr-table-cell__spacer'} />
                                            <span
                                                className={'cr-table-cell__slot page-file-row page-file-row--selectable'}
                                                onClick={(e) => {
                                                    // The whole row toggles its checkbox, except for the row's own controls.
                                                    if (!(e.target as Element).closest('button, a, input, label')) {
                                                        select();
                                                    }
                                                }}
                                            >
                                                <input
                                                    id={`${card.cardKey}-${file.id}`}
                                                    type={'checkbox'}
                                                    className={'cr-checkbox'}
                                                    disabled={file.file.isDeleted}
                                                    checked={file.selected}
                                                    onChange={select}
                                                />
                                                <span className={'page-file-row__info'}>
                                                    <h2 className={'page-file-row__name'}>{file.file.isDeleted ? StringFormat(props.submissionDetailsText?.templateFileDeletedByRetention || '{0} (deleted due to retention policy)', file.file.filename) : file.file.filename}</h2>
                                                </span>
                                                <span className={'page-file-row__size'}>
                                                    {file.file.fileSize ? (file.file.fileSize / 1024).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}
                                                    {' '}
                                                    <span className={'page-file-row__unit'}>{props.submissionDetailsText?.sizeUnitKB || 'KB'}</span>
                                                </span>
                                            </span>
                                            <span className={'cr-table-cell__spacer'} />
                                        </div>
                                    );
                                })}
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </div>
        );
    }

    const renderUserFiles = (): React.ReactElement => {
        return renderFileCard({
            cardKey: 'user_files',
            title: props.submissionDetailsText?.userUploadedFiles || 'User uploaded files',
            columnLabel: props.submissionDetailsText?.columnFile || 'File',
            files: props.documents.map((document, index) => {
                return {
                    file: document,
                    selected: !!((index >= 0) && (index < fileGroupSelections[userUploadedIndex].selections.length) && (fileGroupSelections[userUploadedIndex].selections[index])),
                    id: index,
                }
            }),
            onSelection: (file) => {
                if (ValidIndexSet(userUploadedIndex, file.id)) {
                    const newSelections = createFileGroupSelectionsCopy();
                    newSelections[userUploadedIndex].selections[file.id] = file.selected;
                    setFileGroupSelections(newSelections);
                }
            },
            onDownload: () => downloadSelectedGroupFiles(userUploadedIndex),
        });
    }

    const renderDocGroupings = (): React.ReactElement => {
        // Workbooks render in the card passed as children; this only covers the user uploaded files and the empty state.
        if (!props.hasWorkbooks && !hasUserFiles()) {
            return <p>{props.submissionDetailsText?.noWorkbooksForSubmission || 'There are no workbooks available for this submission. You may need to try re-importing financial data for this company.'}</p>;
        }
        return hasUserFiles() ? renderUserFiles() : <React.Fragment />;
    }

    const detailsList = (
        <FileGroupSubmissionDetailsSummary
            loadedUserDisplayName={props.loadedUserDisplayName}
            submissionDetailsText={props.submissionDetailsText}
            uploadDate={props.uploadDate}
            submittedBy={props.submittedBy}
            submissionRequestSummary={props.submissionRequestSummary}
            submission={props.submission}
            commonText={props.commonText}
            retentionPolicySummary={props.retentionPolicySummary}
            submissionMetadata={props.submissionMetadata}
        />
    );

    const { detailsDrawerOpen, onCloseDetailsDrawer } = props;

    React.useEffect(() => {
        if (!detailsDrawerOpen) {
            return;
        }
        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                onCloseDetailsDrawer && onCloseDetailsDrawer();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [detailsDrawerOpen, onCloseDetailsDrawer]);

    const detailsTitle = props.submissionDetailsText?.collectionDetails || 'Collection details';

    return (
        <div className={'page-overview-layout page-overview-layout--sidebar'}>
            <div className={'page-overview-layout__main'}>
                { props.children }
                { renderDocGroupings() }
            </div>
            <aside className={'page-details-sidebar'} aria-label={detailsTitle}>
                <h2 className={'page-details-sidebar__title'}>{detailsTitle}</h2>
                {detailsList}
            </aside>
            {
                !!detailsDrawerOpen && (
                    <div
                        className={'page-drawer-layer'}
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) {
                                onCloseDetailsDrawer && onCloseDetailsDrawer();
                            }
                        }}
                    >
                        <div className={'cr-drawer'} role={'dialog'} aria-modal={true} aria-labelledby={'details-drawer-title'}>
                            <div className={'cr-drawer__header'}>
                                <h2 className={'cr-drawer__title'} id={'details-drawer-title'}>{detailsTitle}</h2>
                                <button
                                    type={'button'}
                                    className={'cr-btn-icon cr-btn-icon--floating'}
                                    aria-label={props.commonText?.closeButton || 'Close'}
                                    onClick={() => onCloseDetailsDrawer && onCloseDetailsDrawer()}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>
                            <div className={'cr-drawer__main'}>
                                <div className={'cr-drawer__content'}>{detailsList}</div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    )
}
